import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin } from 'lucide-react';
import { Restaurant } from '../../lib/types';
import { isRestaurantOpenBySchedule } from '../../lib/schedule';
interface RestaurantCardProps {
  restaurant: Restaurant;
  zoneName?: string;
  tags?: string[];
}
export function RestaurantCard({
  restaurant,
  zoneName,
  tags = []
}: RestaurantCardProps) {
  const scheduleStatus = isRestaurantOpenBySchedule(restaurant.slug);
  const isActuallyOpen = scheduleStatus.hasSchedule ? scheduleStatus.isOpen : false;
  const isCapricho = restaurant.slug === 'capricho-de-canela' || String(restaurant.id) === '2';
  const isKabu = restaurant.slug === 'kabu';
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const primary = isCapricho ? '#8B5E3C' : isKabu ? '#D4A853' : isEmilius ? '#D42B2B' : isCongelados ? '#7B2D8E' : restaurant.primary_color || '#C9A34E';
  const secondary = restaurant.secondary_color || '#FFFFFF';
  const accent = isCapricho ? '#C8A96A' : isKabu ? '#E8751A' : isEmilius ? '#D42B2B' : isCongelados ? '#F472B6' : restaurant.accent_color || primary;
  const isCrackPollo = restaurant.slug === 'crack-pollo' || String(restaurant.id) === '20';
  const coverUrl = isCapricho ? 'https://cdn.magicpatterns.com/uploads/1PV5hU3phUPpmdu51L1wAD/27E9AE19-B896-4B56-82B1-CEE31AD5C925.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/24WhrxVDroFFjBpfomMhk3/ChatGPT_Image_26_abr_2026,_23_23_00.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vkpirHwDmy1iyMJzGb5usA/ChatGPT_Image_30_abr_2026,_00_44_20.png' : restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/9aYwqfVJGQEzKkCYeBBzcT/MX.jpg';
  const logoUrl = isCapricho ? 'https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/24WhrxVDroFFjBpfomMhk3/ChatGPT_Image_26_abr_2026,_23_23_00.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vrdnrQseiRNenTxSZL7mGp/ChatGPT_Image_30_abr_2026,_00_42_25.png' : restaurant.logo_url;
  return <Link to={`/r/${restaurant.slug}`} className="block group" data-id="element-1464">
      <div className="relative h-[300px] rounded-2xl overflow-hidden border border-white/5 bg-[#141414] transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(201,163,78,0.12)]" data-id="element-1465">
        {/* Cover Image — uses cover_url, or a vibrant fallback */}
        <img src={coverUrl} alt={restaurant.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" data-id="element-1466" />

        {/* Dark gradient overlay — stronger at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" data-id="element-1467" />

        {/* Colored accent glow at bottom — uses restaurant's primary color */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20 pointer-events-none" style={{
        background: `linear-gradient(to top, ${primary}40, transparent)`
      }} data-id="element-1468" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between z-10" data-id="element-1469">
          <div className="flex flex-col gap-1.5" data-id="element-1470">
            {restaurant.is_featured && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{
            backgroundColor: primary,
            color: '#0B0B0B'
          }} data-id="element-1471">
                ★ Destacado
              </span>}
            {isCapricho && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white shadow-lg" data-id="element-1472">
                🎉 2do al 50%
              </span>}
            {isCongelados && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-lg" style={{
            background: 'linear-gradient(135deg, #7B2D8E, #F472B6)'
          }} data-id="element-1473">
                🍦 2do con 10% OFF
              </span>}
          </div>

          {!isActuallyOpen ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/90 text-white" data-id="element-1474">
              Cerrado
            </span> : <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60" data-id="element-1475">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-1476" />
              <span className="text-[10px] font-medium text-white/80" data-id="element-1477">
                Abierto
              </span>
            </span>}
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10" data-id="element-1478">
          <div className="flex items-end gap-3" data-id="element-1479">
            {/* Logo */}
            <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border-2" style={{
            borderColor: `${primary}60`,
            backgroundColor: isCongelados ? '#FFFFFF' : '#0B0B0B'
          }} data-id="element-1480">
              {logoUrl ? <img src={logoUrl} alt={`${restaurant.name} logo`} className={`w-full h-full ${isCapricho ? 'object-contain bg-[#FBF7F2] p-0.5' : isKabu ? 'object-contain bg-[#E0B87B] p-0.5' : isEmilius ? 'object-contain bg-[#F2E6D5] p-0.5' : isCongelados ? 'object-contain bg-white p-0.5' : isCrackPollo ? 'object-contain bg-white p-0.5' : 'object-cover'}`} loading="lazy" decoding="async" data-id="element-1481" /> : <div className="w-full h-full flex items-center justify-center font-bold text-lg" style={{
              backgroundColor: primary,
              color: secondary
            }} data-id="element-1482">
                  {restaurant.name.charAt(0)}
                </div>}
            </div>

            {/* Text content — name always white for readability */}
            <div className="flex-1 min-w-0" data-id="element-1483">
              <h3 className="font-bold text-lg leading-tight line-clamp-1 mb-0.5 text-white" data-id="element-1484">
                {restaurant.name}
              </h3>
              <p className="text-white/60 text-xs line-clamp-1" data-id="element-1485">
                {restaurant.description_short || 'Deliciosa comida preparada con amor.'}
              </p>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10" data-id="element-1486">
            <div className="flex items-center gap-3" data-id="element-1487">
              <div className="flex items-center gap-1 text-white/50" data-id="element-1488">
                <MapPin className="w-3 h-3" data-id="element-1489" />
                <span className="text-[11px] font-medium" data-id="element-1490">
                  {zoneName || restaurant.address_text || 'Los Santos'}
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1491">
                <Star className="w-3 h-3" style={{
                color: primary,
                fill: primary
              }} data-id="element-1492" />
                <span className="text-[11px] font-semibold" style={{
                color: primary
              }} data-id="element-1493">
                  4.8
                </span>
              </div>
            </div>

            <span className="text-xs font-bold transition-colors" style={{
            color: accent
          }} data-id="element-1494">
              {isActuallyOpen ? 'Ordenar →' : 'Ver menú →'}
            </span>
          </div>
        </div>
      </div>
    </Link>;
}